import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Crown, Sparkles, Wallet } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import {
  PROMO_DURATIONS,
  formatAzn,
  promoPrice,
  useBalance,
  WHATSAPP_LINK,
  type PromoPlan,
} from "@/lib/balance";

type Props = {
  adId: string;
  open: boolean;
  plan: PromoPlan;
  days: number;
  onOpenChange: (open: boolean) => void;
};

export function PromoteDialog({ adId, open, plan, days, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: balance } = useBalance(user?.id);
  const [selPlan, setSelPlan] = useState<PromoPlan>(plan);
  const [selDays, setSelDays] = useState<number>(days);

  useEffect(() => {
    if (open) {
      setSelPlan(plan);
      setSelDays(days);
    }
  }, [open, plan, days]);

  const price = promoPrice(selPlan, selDays);
  const enough = Number(balance ?? 0) >= price;

  const go = () => {
    onOpenChange(false);
    void navigate({
      to: "/elan-reklam/$id",
      params: { id: adId },
      search: { plan: selPlan, days: selDays },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle>Elanı önə çıxar</DialogTitle>
          <DialogDescription>Paket və müddət seçin, ödəniş balansdan çıxılır.</DialogDescription>
        </DialogHeader>

        {user && (
          <div className="flex items-center justify-between rounded-2xl border border-border p-3 text-sm">
            <span className="flex items-center gap-2 text-muted-foreground">
              <Wallet className="size-4" /> Balansınız
            </span>
            <span className="font-extrabold">{formatAzn(balance ?? 0)}</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setSelPlan("vip")}
            className={cn(
              "rounded-2xl border-2 p-3 text-left transition",
              selPlan === "vip" ? "border-vip bg-vip/10" : "border-border",
            )}
          >
            <span className="flex items-center gap-1.5 text-sm font-extrabold">
              <Crown className="size-4" /> VIP
            </span>
            <span className="mt-1 block text-xs text-muted-foreground">Üst sıralar, VIP nişanı</span>
          </button>
          <button
            type="button"
            onClick={() => setSelPlan("premium")}
            className={cn(
              "rounded-2xl border-2 p-3 text-left transition",
              selPlan === "premium" ? "border-premium bg-premium/10" : "border-border",
            )}
          >
            <span className="flex items-center gap-1.5 text-sm font-extrabold">
              <Sparkles className="size-4" /> Premium
            </span>
            <span className="mt-1 block text-xs text-muted-foreground">Ən üst sıra və statistika</span>
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {PROMO_DURATIONS.map((d) => (
            <button
              key={d.days}
              type="button"
              onClick={() => setSelDays(d.days)}
              className={cn(
                "rounded-2xl border-2 p-3 text-center transition",
                selDays === d.days ? "border-primary bg-primary/10" : "border-border",
              )}
            >
              <span className="block text-xs text-muted-foreground">{d.label}</span>
              <span className="mt-0.5 block text-lg font-black">
                {promoPrice(selPlan, d.days)} AZN
              </span>
            </button>
          ))}
        </div>

        {user && !enough && (
          <p className="rounded-2xl bg-destructive/10 p-3 text-xs text-destructive">
            Balansınız kifayət etmir. Balans artırmaq üçün{" "}
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="font-bold underline">
              WhatsApp-a yazın
            </a>
            .
          </p>
        )}

        <Button type="button" onClick={go} className="h-12 w-full rounded-2xl text-base font-bold">
          {user ? `${price} AZN — davam et` : "Davam etmək üçün daxil ol"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}